import React from 'react';
import OrderItemCard from './OrderItemCard';
import { 
    TruckIcon, 
    BuildingStorefrontIcon
} from '@heroicons/react/24/outline'; 
import { 
    useCancelFulfillmentGroupMutation,
    useConfirmFulfillmentGroupMutation,
    useGetFulfillmentGroupDetailsQuery
} from '../../../redux/apis/endpoints/fulfillmentGroupApi'; // Updated to use correct API
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../ui/LoadingSpinner';
import Modal from '../../ui/Modal'; // If needed for code input etc.
import Button from '../../ui/Button'; // Using a common Button component
import { useHistory } from 'react-router-dom'; // Add useHistory import
import { useSelector } from 'react-redux';

// Import our new action system
import useActionMatrix from '../../../hooks/useActionMatrix';
import useActionHandler from '../../../hooks/useActionHandler';
import ActionButtonRenderer from '../../actions/shared/ActionButtonRenderer';
import ActionModalRenderer from '../../actions/shared/ActionModalRenderer';
import { FulfillmentGroupStatusBadge } from '../../actions/shared/StatusBadge';
// Import cancel info display component
import CancelInfoDisplay from '../../actions/shared/CancelInfoDisplay';

import FulfillmentGroupActionButtons from './FulfillmentGroupActionButtons'; // Import the new component

const userLang = navigator.language || navigator.userLanguage;

const getFulfillmentGroupStatusText = (status, groupType) => {
  const statusLower = status?.toLowerCase() || 'unknown';
  let defaultText = statusLower.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  // Make certain status texts more concise in English
  if (statusLower === 'shipped_to_customer') defaultText = 'Shipped';
  if (statusLower === 'delivery_completed_to_customer') defaultText = 'Delivered to Customer';
  if (statusLower === 'pickup_completed_by_customer') defaultText = 'Picked Up by Customer';
  
  if (userLang === 'zh-CN') {
    const translations = {
        'coordinating_pickup': '协调取货中',
        'ready_for_pickup': '可供取货',
        'shipped_to_customer': '已发运',
        'pickup_completed_by_customer': '已取货',
        'delivery_completed_to_customer': '已送达',
        'partially_delivered_to_customer': '部分派送',
        'awaiting_return_shipment': '等待退货发运',
        'awaiting_return_dropoff': '等待退货交回',
        'return_in_transit_to_merchant': '退货运输中',
        'return_delivered_to_merchant': '退货已送达商家',
        'return_processing_by_merchant': '商家处理退货中',
        'return_completed_for_group': '退货已完成',
        'canceled': '已取消'
    };
    return translations[statusLower] || defaultText;
  }
  return defaultText;
};

const getFulfillmentGroupStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (FG_ONGOING_STATUSES.includes(statusLower)) return 'blue';
    if (FG_COMPLETED_STATUSES.includes(statusLower)) return 'green';
    if (statusLower === FG_CANCELED_STATUS) return 'red';
    if (statusLower && statusLower.includes('return')) return 'orange';
    return 'gray';
};

// Constants defined in Transactions.js, redefined here for modularity or import from a shared constants file
const FG_ONGOING_STATUSES = [
  'processing', 'coordinating_pickup', 'ready_for_pickup', 
  'shipped_to_customer', 'partially_delivered_to_customer', 'awaiting_return_shipment', 
  'awaiting_return_dropoff', 'return_in_transit_to_merchant', 'return_processing_by_merchant'
];
const FG_COMPLETED_STATUSES = ['pickup_completed_by_customer', 'delivery_completed_to_customer', 'return_completed_for_group'];
const FG_CANCELED_STATUS = 'canceled';

const FulfillmentGroupCard = ({ fulfillmentGroup: propFulfillmentGroup, orderId, orderNumber, viewContext, orderSource, parentOrder }) => {
  // --- ALL HOOKS MUST BE CALLED FIRST ---
  const history = useHistory();
  const user = useSelector(state => state.auth.user);
  
  // Use RTK Query to subscribe to fulfillment group data for real-time updates
  // This ensures the component re-renders when cache is invalidated by mutations
  const { 
    data: latestFulfillmentGroup, 
    isLoading: isFetchingFG,
    error: fetchError 
  } = useGetFulfillmentGroupDetailsQuery(propFulfillmentGroup?.id, {
    skip: !propFulfillmentGroup?.id, // Skip if no ID provided
    // Refetch every 30 seconds to ensure fresh data
    refetchOnMountOrArgChange: true,
  });

  // Use the latest data from RTK Query, fallback to props if not available yet
  const fulfillmentGroup = latestFulfillmentGroup || propFulfillmentGroup;
  
  // Debug logging for cache updates
  React.useEffect(() => {
    if (latestFulfillmentGroup && propFulfillmentGroup) {
      console.log('🔄 FulfillmentGroupCard cache update detected:', {
        fgId: propFulfillmentGroup.id,
        propStatus: propFulfillmentGroup.status,
        cacheStatus: latestFulfillmentGroup.status,
        statusChanged: propFulfillmentGroup.status !== latestFulfillmentGroup.status,
        orderItemsCount: latestFulfillmentGroup.orderItems?.length
      });
    }
  }, [latestFulfillmentGroup, propFulfillmentGroup]);

  const [cancelFulfillmentGroup, { isLoading: isCancelingGroup }] = useCancelFulfillmentGroupMutation();
  const [confirmFulfillmentGroup, { isLoading: isConfirmingDelivery }] = useConfirmFulfillmentGroupMutation();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalAction, setModalAction] = React.useState(null);

  // Determine user role for action matrix - this needs to be before useActionMatrix
  const userRole = React.useMemo(() => {
    if (!fulfillmentGroup) return 'customer'; // Default value for early return case
    if (viewContext === 'selling' || viewContext === 'lending' || 
        (viewContext === 'all' && orderSource === 'merchant')) {
      return 'merchant';
    }
    return 'customer';
  }, [viewContext, orderSource, fulfillmentGroup]);

  // Use our new action matrix system - pass empty values for early return case
  const availableActions = useActionMatrix(
    fulfillmentGroup || {},
    'fulfillmentGroup',
    userRole
  );

  // Check if products in this fulfillment group have been reviewed
  const hasProductReviews = React.useMemo(() => {
    if (!fulfillmentGroup?.orderItems) return false;
    return fulfillmentGroup.orderItems.some(item => 
      item.reviewStatus === 'reviewed' || item.review
    );
  }, [fulfillmentGroup?.orderItems]);

  // Modify the review action to have dynamic variant based on review status
  const processedActions = React.useMemo(() => {
    return availableActions.map(action => {
      if (action.id === 'review_products') {
        return {
          ...action,
          type: hasProductReviews ? 'secondary' : 'yellow', // beige if reviewed, yellow if not
          label: hasProductReviews ? 'Update Review' : 'Review Products'
        };
      }
      return action;
    });
  }, [availableActions, hasProductReviews]);

  // Use action handler hook
  const { handleAction, activeModals } = useActionHandler();

  // Early return AFTER all hooks have been called
  if (!fulfillmentGroup) {
    if (isFetchingFG) {
      return (
        <div className="border-t border-gray-200 p-4 flex justify-center">
          <LoadingSpinner />
        </div>
      );
    }
    if (fetchError) {
      return (
        <div className="border-t border-gray-200 p-4 text-red-600 text-sm">
          Error loading fulfillment group: {fetchError.message}
        </div>
      );
    }
    return null;
  }

  // Destructure fulfillmentGroup properties after hooks and null check
  const { 
    id: fgId,
    groupType, 
    status, 
    merchant, 
    orderItems = [], 
    pickupLocationAddress, 
    shippingOriginAddress,
    outboundDeliveries = [],
    returnDeliveries = [],
  } = fulfillmentGroup;

  const statusText = getFulfillmentGroupStatusText(status, groupType);
  const statusColorName = getFulfillmentGroupStatusColor(status);

  // Legacy action handler (keeping for specific navigation and complex actions)
  const handleGroupAction = async (action) => {
    console.log('Handling group action:', action, 'for FG:', fgId);
    
    switch (action.id) {
      case 'start_shipping':
      case 'add_shipment_details':
        history.push(`/merchant/ship/${fgId}`);
        break;
      case 'contact_user':
        history.push(`/message/fg/${fgId}`);
        break;
      case 'view_pickup_location':
        // Navigate to pickup location details
        if (pickupLocationAddress) {
          toast.info('Showing pickup location details');
        }
        break;
      default:
        // Use the new action handler for other actions
        try {
          // Prepare mutation hooks for the action handler
          const mutationHooks = {
            confirmFulfillmentGroup: [confirmFulfillmentGroup],
            cancelFulfillmentGroup: [cancelFulfillmentGroup]
          };
          
          // Prepare entity data with necessary IDs
          const entityWithIds = {
            ...fulfillmentGroup,
            orderId: orderId || parentOrder?.id,
            groupId: fgId
          };
          
          const result = await handleAction(action, entityWithIds, userRole, mutationHooks);
          
          // Only show success toast for actual mutations, not redirects
          if (result.success && !result.redirected) {
            toast.success(`${action.label} completed successfully`);
          } else if (!result.cancelled && !result.redirected) {
            toast.error(`Failed to ${action.label.toLowerCase()}: ${result.error || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error handling action:', error);
          toast.error(`Failed to ${action.label.toLowerCase()}: ${error.message}`);
        }
        break;
    }
  };

  const showConfirmationModal = (actionDetails) => {
    setModalAction(actionDetails);
    setIsModalOpen(true);
  };

  const handleConfirmModal = () => {
    if (modalAction && modalAction.type) {
      handleGroupAction(modalAction.type, modalAction.payload || {});
    }
    setIsModalOpen(false);
    setModalAction(null);
  };

  return (
    <div className="border-t border-gray-200">
      {/* Header Section */}
      <div className="bg-gray-50 px-8 py-3 sm:px-6 flex flex-wrap items-center justify-center sm:justify-between gap-x-4 gap-y-2 border-b border-gray-200">
        <div className="flex items-center gap-2">
          {groupType === 'shipping' ? 
            <TruckIcon className="h-5 w-5 text-gray-500 flex-shrink-0" /> : 
            <BuildingStorefrontIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
          }
          <span className="text-sm font-medium text-orange-700 bg-orange-100 px-2 py-0.5 rounded-md">
            {groupType === 'shipping' ? (userLang === 'zh-CN' ? '配送' : 'Shipping') : (userLang === 'zh-CN' ? '自提' : 'Pickup')}
          </span>
          
          {/* Status Badge */}
          <FulfillmentGroupStatusBadge 
            fulfillmentGroup={fulfillmentGroup} 
            className="text-xs"
          />
        </div>
      </div>

      {/* Order Items */}
      <div className="px-4 py-4 space-y-4">
        {orderItems.map((orderItem) => (
          <OrderItemCard
            key={orderItem.id}
            orderItem={orderItem}
            fulfillmentGroup={fulfillmentGroup}
            order={parentOrder}
            viewContext={viewContext}
          />
        ))}
      </div>

      {/* Cancel Information Display */}
      <CancelInfoDisplay 
        entity={fulfillmentGroup}
        entityType="group"
        className="mx-4 mb-4"
        size="sm"
      />

      {/* Action Buttons Section at Bottom */}
      {status !== 'canceled' && parentOrder?.status !== 'canceled' && (
        <div className="px-4 pb-4">
          <ActionButtonRenderer
            actions={processedActions}
            layout="horizontal"
            size="sm"
            onActionClick={handleGroupAction}
            isLoading={Object.keys(activeModals).length > 0 || isCancelingGroup || isConfirmingDelivery}
            className="flex flex-wrap gap-2 justify-center sm:justify-end"
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalAction?.title || 'Confirm Action'}
      >
        <div className="p-4">
          <p className="text-gray-700 mb-4">
            {modalAction?.message || 'Are you sure you want to proceed?'}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              {userLang === 'zh-CN' ? '取消' : 'Cancel'}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmModal}
              isLoading={Object.keys(activeModals).length > 0 || isCancelingGroup || isConfirmingDelivery}
            >
              {userLang === 'zh-CN' ? '确认' : 'Confirm'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Action Modals */}
      <ActionModalRenderer activeModals={activeModals} />
    </div>
  );
};

export default FulfillmentGroupCard; 